import React from 'react';
import { X, Shield, Database, Cookie, Mail, Globe } from 'lucide-react';

const Datenschutz = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-700 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-cyan-400" />
            <h2 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-amber-500 bg-clip-text text-transparent">
              Datenschutzerklärung
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-8 text-gray-300">
          {/* Einleitung */}
          <section>
            <p className="text-sm leading-relaxed mb-4">
              Der Schutz Ihrer persönlichen Daten ist uns ein besonderes Anliegen. Wir verarbeiten Ihre 
              Daten daher ausschließlich auf Grundlage der gesetzlichen Bestimmungen (DSGVO, TKG 2003). 
              In diesen Datenschutzinformationen informieren wir Sie über die wichtigsten Aspekte der 
              Datenverarbeitung im Rahmen unserer Website.
            </p>
          </section>

          {/* Verantwortlicher */}
          <section>
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-cyan-400" />
              Verantwortlicher
            </h3>
            <div className="text-sm space-y-1">
              <p>Lazzaro One UG (haftungsbeschränkt)</p>
              <p>Arsenio Longo</p>
              <p>Forckenbeckstraße 63c</p>
              <p>14199 Berlin</p>
              <p>E-Mail: <a href="mailto:office@lazzaro.one" className="text-cyan-400 hover:underline">office@lazzaro.one</a></p>
              <p>Telefon: <a href="tel:+493024618457" className="text-cyan-400 hover:underline">+49 (0)30 24618457</a></p>
            </div>
          </section>

          {/* Datenerfassung */}
          <section>
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-400" />
              Datenerfassung auf unserer Website
            </h3>
            
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-semibold text-white mb-2">Server-Log-Dateien</h4>
                <p className="leading-relaxed">
                  Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten 
                  Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                  <li>Browsertyp und Browserversion</li>
                  <li>Verwendetes Betriebssystem</li>
                  <li>Referrer URL</li>
                  <li>Hostname des zugreifenden Rechners</li>
                  <li>Uhrzeit der Serveranfrage</li>
                  <li>IP-Adresse</li>
                </ul>
                <p className="mt-2 leading-relaxed">
                  Diese Daten werden nicht mit anderen Datenquellen zusammengeführt. Die Grundlage für die 
                  Datenverarbeitung ist Art. 6 Abs. 1 lit. f DSGVO, der die Verarbeitung von Daten zur 
                  Erfüllung eines Vertrags oder vorvertraglicher Maßnahmen gestattet.
                </p>
              </div>
            </div>
          </section>

          {/* Cookies */}
          <section>
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Cookie className="w-5 h-5 text-cyan-400" />
              Cookies
            </h3>
            <div className="text-sm space-y-4">
              <p className="leading-relaxed">
                Unsere Website verwendet so genannte Cookies. Dabei handelt es sich um kleine Textdateien, 
                die mit Hilfe des Browsers auf Ihrem Endgerät abgelegt werden. Sie richten keinen Schaden an.
              </p>
              <p className="leading-relaxed">
                Wir nutzen Cookies dazu, unser Angebot nutzerfreundlich zu gestalten. Einige Cookies bleiben 
                auf Ihrem Endgerät gespeichert, bis Sie diese löschen. Sie ermöglichen es uns, Ihren Browser 
                beim nächsten Besuch wiederzuerkennen.
              </p>
              <div>
                <h4 className="font-semibold text-white mb-2">Arten von Cookies:</h4>
                <ul className="space-y-2">
                  <li>
                    <span className="font-medium text-white">Notwendige Cookies:</span>
                    <span className="text-gray-400"> Für die Grundfunktionen der Website erforderlich</span>
                  </li>
                  <li>
                    <span className="font-medium text-white">Funktionale Cookies:</span>
                    <span className="text-gray-400"> Speichern Ihre Präferenzen (z.B. Spracheinstellung)</span>
                  </li>
                  <li>
                    <span className="font-medium text-white">Analyse-Cookies:</span>
                    <span className="text-gray-400"> Helfen uns, die Nutzung unserer Website zu verstehen</span>
                  </li>
                </ul>
              </div>
              <p className="leading-relaxed">
                Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden 
                und Cookies nur im Einzelfall erlauben, die Annahme von Cookies für bestimmte Fälle oder 
                generell ausschließen sowie das automatische Löschen der Cookies beim Schließen des Browsers aktivieren.
              </p>
            </div>
          </section>

          {/* Kontaktformular */}
          <section>
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-cyan-400" />
              Kontaktaufnahme
            </h3>
            <div className="text-sm space-y-4">
              <p className="leading-relaxed">
                Wenn Sie per E-Mail oder Telefon Kontakt mit uns aufnehmen, werden Ihre angegebenen Daten 
                zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. 
                Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
              </p>
              <p className="leading-relaxed">
                Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern 
                Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung 
                vorvertraglicher Maßnahmen erforderlich ist.
              </p>
            </div>
          </section>

          {/* Ihre Rechte */}
          <section>
            <h3 className="text-xl font-semibold text-white mb-4">Ihre Rechte</h3>
            <div className="text-sm space-y-4">
              <p className="leading-relaxed">
                Ihnen stehen grundsätzlich die Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung, 
                Datenübertragbarkeit, Widerruf und Widerspruch zu. Wenn Sie glauben, dass die Verarbeitung 
                Ihrer Daten gegen das Datenschutzrecht verstößt oder Ihre datenschutzrechtlichen Ansprüche 
                sonst in einer Weise verletzt worden sind, können Sie sich bei der Aufsichtsbehörde beschweren.
              </p>
              <div>
                <h4 className="font-semibold text-white mb-2">Sie haben das Recht:</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-400">
                  <li>Auskunft über Ihre gespeicherten personenbezogenen Daten zu erhalten</li>
                  <li>Berichtigung unrichtiger Daten zu verlangen</li>
                  <li>Löschung Ihrer Daten zu verlangen</li>
                  <li>Einschränkung der Verarbeitung zu verlangen</li>
                  <li>Widerspruch gegen die Verarbeitung einzulegen</li>
                  <li>Datenübertragbarkeit zu verlangen</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Kontakt Datenschutz */}
          <section>
            <h3 className="text-xl font-semibold text-white mb-4">Kontakt für Datenschutzfragen</h3>
            <div className="text-sm">
              <p className="mb-2">Bei Fragen zum Datenschutz wenden Sie sich bitte an:</p>
              <p>E-Mail: <a href="mailto:datenschutz@lazzaro.one" className="text-cyan-400 hover:underline">datenschutz@lazzaro.one</a></p>
              <p>Telefon: <a href="tel:+493024618457" className="text-cyan-400 hover:underline">+49 (0)30 24618457</a></p>
            </div>
          </section>

          {/* Stand */}
          <section className="pt-4 border-t border-gray-700">
            <p className="text-sm text-gray-500">Stand: Januar 2025</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Datenschutz;